import { useCallback, useSyncExternalStore } from 'react';
import { useStore } from 'react-redux';
import { fuel } from '@/lib/trackizy';

const REFRESH_THROTTLE_MS = 5000;
const POLL_MS = 60_000;

/**
 * One shared Fuel overview subscription for the whole app: the Live Map list, the telemetry card,
 * replay charts and the Fuel page all read the same data, started on the first subscriber and
 * stopped on the last. A new fix for any listed device (redux session.positions) refetches at most
 * once per throttle window; a 60 s poll catches what no position announces (no-data).
 * Only the store below reads session.positions, so consumers never re-render on position batches.
 */
const shared = {
  snapshot: { data: null, error: '' },
  listeners: new Set(),
  interval: null,
  throttleTimer: null,
  unsubRedux: null,
  reduxStore: null,
  orgId: null,
  lastPositions: null,
  seen: new Map(),
  reqId: 0,
};

function emit(next) {
  shared.snapshot = { ...shared.snapshot, ...next };
  shared.listeners.forEach((l) => l());
}

async function load() {
  const id = ++shared.reqId;
  try {
    const next = await fuel.overview();
    if (id !== shared.reqId) return; // a newer request is in flight or done — never let an older reply overwrite it
    emit({ data: next, error: '' });
  } catch (e) {
    if (id === shared.reqId) emit({ error: e?.message || 'error' });
  }
}

function onReduxChange() {
  // A different org (e.g. a sultan switching organization without a reload): never show the old one's vehicles.
  const orgId = shared.reduxStore?.getState().org?.id ?? null;
  if (orgId !== shared.orgId) {
    shared.orgId = orgId;
    shared.reqId++; // drop any reply still in flight for the old org
    shared.seen.clear();
    clearTimeout(shared.throttleTimer);
    shared.throttleTimer = null;
    emit({ data: null, error: '' });
    load();
    return;
  }
  const positions = shared.reduxStore?.getState().session?.positions;
  if (positions === shared.lastPositions) return;
  shared.lastPositions = positions;
  // Only profiled vehicles have a level that a new fix can change; an unconfigured one never triggers a refetch.
  const vehicles = shared.snapshot.data?.vehicles?.filter((v) => v.profileId);
  if (!vehicles?.length) return;
  let changed = false;
  for (const deviceId of new Set(vehicles.map((v) => v.deviceId))) {
    const fix = positions?.[deviceId]?.fixTime;
    if (!fix) continue;
    if (shared.seen.has(deviceId) && shared.seen.get(deviceId) !== fix) changed = true;
    shared.seen.set(deviceId, fix);
  }
  // Throttle, not debounce: a pending refresh is never pushed back.
  if (!changed || shared.throttleTimer) return;
  shared.throttleTimer = setTimeout(() => { shared.throttleTimer = null; load(); }, REFRESH_THROTTLE_MS);
}

function start(reduxStore) {
  shared.reduxStore = reduxStore;
  shared.orgId = reduxStore.getState().org?.id ?? null;
  shared.lastPositions = reduxStore.getState().session?.positions;
  shared.unsubRedux = reduxStore.subscribe(onReduxChange);
  load();
  shared.interval = setInterval(load, POLL_MS);
}

function stop() {
  clearInterval(shared.interval);
  clearTimeout(shared.throttleTimer);
  shared.unsubRedux?.();
  Object.assign(shared, { interval: null, throttleTimer: null, unsubRedux: null, reduxStore: null, orgId: null, lastPositions: null });
  shared.seen.clear();
  shared.snapshot = { data: null, error: '' }; // never carry one org's vehicles into the next mount
  shared.reqId++; // drop any reply still in flight
}

const noopSubscribe = () => () => {};
const DISABLED = { data: null, error: '' };
const getSnapshot = () => shared.snapshot;
const getDisabled = () => DISABLED;

export default function useFuelOverview({ enabled = true } = {}) {
  const reduxStore = useStore();
  const subscribe = useCallback((listener) => {
    shared.listeners.add(listener);
    if (shared.listeners.size === 1) start(reduxStore);
    return () => {
      shared.listeners.delete(listener);
      if (shared.listeners.size === 0) stop();
    };
  }, [reduxStore]);
  const { data, error } = useSyncExternalStore(enabled ? subscribe : noopSubscribe, enabled ? getSnapshot : getDisabled);
  const reload = useCallback(() => (enabled ? load() : undefined), [enabled]);
  return { data, error, reload };
}
